/**
 * Fase B — Admin sections voor verzendkosten + kortingscodes.
 *
 * Wordt gemount als nieuwe tab(s) binnen Webshopbeheer. Pure CRUD-UI,
 * minimaal maar functioneel. Geen complex form-builder — alleen wat de
 * acceptance vraagt: naam/bedrag/landen/min/free-above/active.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Tag, Truck } from "lucide-react";

import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Checkbox } from "../../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../ui/dialog";
import { adminApi } from "../../../context/AuthContext";

const fmtEur = (cents) => `€ ${(Number(cents || 0) / 100).toFixed(2)}`;

// ============================================================
// SHIPPING METHODS
// ============================================================
const EMPTY_METHOD = {
  name: "", description: "", amount_cents: 0, currency: "eur",
  countries: [], min_order_cents: 0, free_above_cents: null,
  active: true, sort_order: 0, pickup: false,
};

export function ShippingMethodsSection() {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // method object of EMPTY_METHOD voor nieuw

  const reload = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/shipping-methods");
      setMethods(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const save = async (m) => {
    try {
      if (m.id) {
        await adminApi.patch(`/admin/shipping-methods/${m.id}`, m);
        toast.success("Verzendmethode bijgewerkt");
      } else {
        await adminApi.post("/admin/shipping-methods", m);
        toast.success("Verzendmethode aangemaakt");
      }
      setEditing(null);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Opslaan mislukt");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Verzendmethode permanent verwijderen?")) return;
    try {
      await adminApi.delete(`/admin/shipping-methods/${id}`);
      toast.success("Verwijderd");
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verwijderen mislukt");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-shipping-methods-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-chivo font-black text-xl text-[#082c50] flex items-center gap-2"><Truck className="w-5 h-5" /> Verzendmethodes</h2>
          <p className="text-xs text-gray-600">Beheer verzendkosten per land en orderbedrag. Klant kiest in de checkout.</p>
        </div>
        <Button onClick={() => setEditing({ ...EMPTY_METHOD })} className="bg-[#082c50] hover:bg-[#06223f] text-white rounded-xl text-xs font-chivo font-bold uppercase" data-testid="admin-shipping-add">
          <Plus className="w-3 h-3 mr-1" /> Methode toevoegen
        </Button>
      </div>
      {loading ? <p className="text-sm text-gray-500">Laden...</p> : methods.length === 0 ? (
        <div className="bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900" data-testid="admin-shipping-empty">
          Nog geen verzendmethodes geconfigureerd. Klanten zien geen verzendoptie.
        </div>
      ) : (
        <table className="w-full text-sm border border-gray-200">
          <thead className="bg-gray-50 text-xs uppercase">
            <tr><th className="py-2 px-3 text-left">Naam</th><th className="py-2 px-3 text-left">Bedrag</th><th className="py-2 px-3 text-left">Landen</th><th className="py-2 px-3 text-left">Min/Free</th><th className="py-2 px-3">Actief</th><th></th></tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {methods.map((m) => (
              <tr key={m.id} data-testid={`admin-shipping-row-${m.id}`}>
                <td className="py-2 px-3">
                  <div className="font-bold">{m.name}</div>
                  {m.pickup && <div className="text-[10px] uppercase text-[#1a5193] font-bold">Afhalen</div>}
                  <div className="text-xs text-gray-500">{m.description}</div>
                </td>
                <td className="py-2 px-3 font-mono">{fmtEur(m.amount_cents)}</td>
                <td className="py-2 px-3 text-xs">{(m.countries || []).join(", ") || "Alle"}</td>
                <td className="py-2 px-3 text-xs">
                  {m.min_order_cents > 0 && <div>Min: {fmtEur(m.min_order_cents)}</div>}
                  {m.free_above_cents !== null && m.free_above_cents !== undefined && <div>Gratis ≥ {fmtEur(m.free_above_cents)}</div>}
                </td>
                <td className="py-2 px-3 text-center">{m.active ? "✓" : "—"}</td>
                <td className="py-2 px-3 text-right">
                  <Button size="sm" variant="outline" onClick={() => setEditing(m)} className="rounded-none text-xs mr-1" data-testid={`admin-shipping-edit-${m.id}`}><Pencil className="w-3 h-3" /></Button>
                  <Button size="sm" variant="outline" onClick={() => remove(m.id)} className="rounded-none text-xs text-red-600" data-testid={`admin-shipping-delete-${m.id}`}><Trash2 className="w-3 h-3" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && <ShippingMethodDialog method={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function ShippingMethodDialog({ method, onClose, onSave }) {
  const [m, setM] = useState({ ...EMPTY_METHOD, ...method, countries: method.countries || [] });
  const setF = (k, v) => setM((cur) => ({ ...cur, [k]: v }));

  const eurField = (k, v) => setF(k, Math.max(0, Math.round(Number(v || 0) * 100)));

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-xl max-w-xl" data-testid="admin-shipping-dialog">
        <DialogHeader>
          <DialogTitle className="font-chivo font-black text-xl text-[#082c50]">{m.id ? "Verzendmethode bewerken" : "Nieuwe verzendmethode"}</DialogTitle>
          <DialogDescription>Naam, bedrag en filters voor deze verzendoptie.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[11px] uppercase font-bold">Naam *</Label>
            <Input data-testid="admin-shipping-name" value={m.name} onChange={(e) => setF("name", e.target.value)} className="rounded-xl mt-1" />
          </div>
          <div>
            <Label className="text-[11px] uppercase font-bold">Omschrijving</Label>
            <Textarea data-testid="admin-shipping-description" rows={2} value={m.description || ""} onChange={(e) => setF("description", e.target.value)} className="rounded-xl mt-1" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-[11px] uppercase font-bold">Bedrag (€)</Label>
              <Input type="number" step="0.01" data-testid="admin-shipping-amount" value={Number(m.amount_cents || 0) / 100} onChange={(e) => eurField("amount_cents", e.target.value)} className="rounded-xl mt-1" />
            </div>
            <div>
              <Label className="text-[11px] uppercase font-bold">Min orderbedrag (€)</Label>
              <Input type="number" step="0.01" data-testid="admin-shipping-min" value={Number(m.min_order_cents || 0) / 100} onChange={(e) => eurField("min_order_cents", e.target.value)} className="rounded-xl mt-1" />
            </div>
            <div>
              <Label className="text-[11px] uppercase font-bold">Gratis ≥ (€)</Label>
              <Input type="number" step="0.01" data-testid="admin-shipping-free"
                value={m.free_above_cents !== null && m.free_above_cents !== undefined ? Number(m.free_above_cents) / 100 : ""}
                placeholder="Leeg = nooit gratis"
                onChange={(e) => setF("free_above_cents", e.target.value === "" ? null : Math.max(0, Math.round(Number(e.target.value) * 100)))}
                className="rounded-xl mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-[11px] uppercase font-bold">Landen (komma's, leeg of "*" = alle)</Label>
            <Input data-testid="admin-shipping-countries" value={(m.countries || []).join(", ")}
              placeholder="Nederland, België"
              onChange={(e) => setF("countries", e.target.value.split(",").map((c) => c.trim()).filter(Boolean))}
              className="rounded-xl mt-1" />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <Checkbox data-testid="admin-shipping-active" checked={m.active} onCheckedChange={(v) => setF("active", Boolean(v))} /> Actief
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <Checkbox data-testid="admin-shipping-pickup" checked={m.pickup} onCheckedChange={(v) => setF("pickup", Boolean(v))} /> Afhalen op locatie
            </label>
            <div className="flex items-center gap-2 text-sm">
              <Label className="text-[11px] uppercase">Volgorde</Label>
              <Input type="number" value={m.sort_order || 0} onChange={(e) => setF("sort_order", parseInt(e.target.value || 0, 10))} className="w-20 rounded-xl" data-testid="admin-shipping-sort" />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 mt-4">
          <Button variant="outline" onClick={onClose} className="rounded-xl text-xs">Annuleren</Button>
          <Button onClick={() => onSave(m)} className="bg-[#082c50] hover:bg-[#06223f] text-white rounded-xl text-xs font-chivo font-bold uppercase" data-testid="admin-shipping-save">Opslaan</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// DISCOUNT CODES
// ============================================================
const EMPTY_DISCOUNT = {
  code: "", type: "percentage", value: 10,
  active: true, starts_at: null, ends_at: null,
  min_order_cents: 0, max_uses: null, max_uses_per_customer: null,
  description: "",
};

export function DiscountCodesSection() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/discount-codes");
      setCodes(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const save = async (d) => {
    try {
      if (d.id) {
        await adminApi.patch(`/admin/discount-codes/${d.id}`, d);
        toast.success("Code bijgewerkt");
      } else {
        await adminApi.post("/admin/discount-codes", d);
        toast.success("Code aangemaakt");
      }
      setEditing(null);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Opslaan mislukt");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Code permanent verwijderen?")) return;
    try {
      await adminApi.delete(`/admin/discount-codes/${id}`);
      toast.success("Verwijderd");
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verwijderen mislukt");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-discount-codes-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-chivo font-black text-xl text-[#082c50] flex items-center gap-2"><Tag className="w-5 h-5" /> Kortingscodes</h2>
          <p className="text-xs text-gray-600">Percentage of vast bedrag, optionele datums en limieten.</p>
        </div>
        <Button onClick={() => setEditing({ ...EMPTY_DISCOUNT })} className="bg-[#082c50] hover:bg-[#06223f] text-white rounded-xl text-xs font-chivo font-bold uppercase" data-testid="admin-discount-add">
          <Plus className="w-3 h-3 mr-1" /> Code toevoegen
        </Button>
      </div>
      {loading ? <p className="text-sm text-gray-500">Laden...</p> : codes.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 p-4 text-sm text-gray-700" data-testid="admin-discount-empty">
          Nog geen kortingscodes ingesteld.
        </div>
      ) : (
        <table className="w-full text-sm border border-gray-200">
          <thead className="bg-gray-50 text-xs uppercase">
            <tr><th className="py-2 px-3 text-left">Code</th><th className="py-2 px-3 text-left">Type</th><th className="py-2 px-3 text-left">Waarde</th><th className="py-2 px-3 text-left">Limieten</th><th className="py-2 px-3">Actief</th><th></th></tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {codes.map((c) => (
              <tr key={c.id} data-testid={`admin-discount-row-${c.id}`}>
                <td className="py-2 px-3"><div className="font-mono font-bold">{c.code}</div>{c.description && <div className="text-xs text-gray-500">{c.description}</div>}</td>
                <td className="py-2 px-3 capitalize">{c.type === "percentage" ? "Percentage" : "Vast bedrag"}</td>
                <td className="py-2 px-3">{c.type === "percentage" ? `${c.value}%` : fmtEur(c.value)}</td>
                <td className="py-2 px-3 text-xs">
                  {c.min_order_cents > 0 && <div>Min: {fmtEur(c.min_order_cents)}</div>}
                  {c.max_uses !== null && c.max_uses !== undefined && <div>Max totaal: {c.max_uses}</div>}
                  {c.max_uses_per_customer !== null && c.max_uses_per_customer !== undefined && <div>Max/klant: {c.max_uses_per_customer}</div>}
                </td>
                <td className="py-2 px-3 text-center">{c.active ? "✓" : "—"}</td>
                <td className="py-2 px-3 text-right">
                  <Button size="sm" variant="outline" onClick={() => setEditing(c)} className="rounded-none text-xs mr-1" data-testid={`admin-discount-edit-${c.id}`}><Pencil className="w-3 h-3" /></Button>
                  <Button size="sm" variant="outline" onClick={() => remove(c.id)} className="rounded-none text-xs text-red-600" data-testid={`admin-discount-delete-${c.id}`}><Trash2 className="w-3 h-3" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && <DiscountCodeDialog code={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function DiscountCodeDialog({ code, onClose, onSave }) {
  const [c, setC] = useState({ ...EMPTY_DISCOUNT, ...code });
  const setF = (k, v) => setC((cur) => ({ ...cur, [k]: v }));
  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-xl max-w-xl" data-testid="admin-discount-dialog">
        <DialogHeader>
          <DialogTitle className="font-chivo font-black text-xl text-[#082c50]">{c.id ? "Code bewerken" : "Nieuwe kortingscode"}</DialogTitle>
          <DialogDescription>Configureer waarde, beperkingen en geldigheid.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase font-bold">Code *</Label>
              <Input data-testid="admin-discount-code" value={c.code} onChange={(e) => setF("code", e.target.value.toUpperCase())} className="rounded-xl mt-1 uppercase font-mono" />
            </div>
            <div>
              <Label className="text-[11px] uppercase font-bold">Type</Label>
              <select className="w-full border border-gray-300 rounded-xl mt-1 px-3 py-2 text-sm" data-testid="admin-discount-type" value={c.type} onChange={(e) => setF("type", e.target.value)}>
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Vast bedrag (€ in cents)</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase font-bold">Waarde * {c.type === "percentage" ? "(1-100 %)" : "(in cents)"}</Label>
              <Input type="number" data-testid="admin-discount-value" value={c.value || ""} onChange={(e) => setF("value", parseInt(e.target.value || 0, 10))} className="rounded-xl mt-1" />
            </div>
            <div>
              <Label className="text-[11px] uppercase font-bold">Min orderbedrag (cents)</Label>
              <Input type="number" data-testid="admin-discount-min" value={c.min_order_cents || 0} onChange={(e) => setF("min_order_cents", Math.max(0, parseInt(e.target.value || 0, 10)))} className="rounded-xl mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase font-bold">Startdatum (optioneel)</Label>
              <Input type="datetime-local" data-testid="admin-discount-start" value={c.starts_at ? c.starts_at.slice(0, 16) : ""} onChange={(e) => setF("starts_at", e.target.value ? new Date(e.target.value).toISOString() : null)} className="rounded-xl mt-1" />
            </div>
            <div>
              <Label className="text-[11px] uppercase font-bold">Einddatum (optioneel)</Label>
              <Input type="datetime-local" data-testid="admin-discount-end" value={c.ends_at ? c.ends_at.slice(0, 16) : ""} onChange={(e) => setF("ends_at", e.target.value ? new Date(e.target.value).toISOString() : null)} className="rounded-xl mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase font-bold">Max gebruik (totaal)</Label>
              <Input type="number" data-testid="admin-discount-maxuses" value={c.max_uses ?? ""} placeholder="Onbeperkt" onChange={(e) => setF("max_uses", e.target.value === "" ? null : parseInt(e.target.value, 10))} className="rounded-xl mt-1" />
            </div>
            <div>
              <Label className="text-[11px] uppercase font-bold">Max per klant</Label>
              <Input type="number" data-testid="admin-discount-maxpercust" value={c.max_uses_per_customer ?? ""} placeholder="Onbeperkt" onChange={(e) => setF("max_uses_per_customer", e.target.value === "" ? null : parseInt(e.target.value, 10))} className="rounded-xl mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-[11px] uppercase font-bold">Omschrijving (optioneel)</Label>
            <Input data-testid="admin-discount-description" value={c.description || ""} onChange={(e) => setF("description", e.target.value)} className="rounded-xl mt-1" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <Checkbox data-testid="admin-discount-active" checked={c.active} onCheckedChange={(v) => setF("active", Boolean(v))} /> Actief
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 mt-4">
          <Button variant="outline" onClick={onClose} className="rounded-xl text-xs">Annuleren</Button>
          <Button onClick={() => onSave(c)} className="bg-[#082c50] hover:bg-[#06223f] text-white rounded-xl text-xs font-chivo font-bold uppercase" data-testid="admin-discount-save">Opslaan</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
